/**
 * SQLite Schema for pyxl Desktop
 *
 * Mirrors the Firestore collection structure:
 *   users/{uid}           → users table
 *   users/{uid}/runs      → runs + level_results tables
 *   leaderboards/{id}     → leaderboard_entries table
 *
 * The sync_queue table stores pending offline actions
 * to be synced when connectivity is restored (M2-DATA-002).
 */

/** Schema version — increment when adding migrations */
export const SCHEMA_VERSION = 1;

/**
 * Initial migration: create all tables.
 * Uses IF NOT EXISTS for idempotent re-runs.
 */
export const MIGRATIONS: string[] = [
  // === v1: Core tables ===

  // Users table — matches Firestore users/{uid}
  `CREATE TABLE IF NOT EXISTS users (
    uid TEXT PRIMARY KEY,
    display_name TEXT NOT NULL DEFAULT '',
    photo_url TEXT,
    created_at TEXT NOT NULL DEFAULT (datetime('now')),
    total_runs INTEGER NOT NULL DEFAULT 0,
    perfect_count INTEGER NOT NULL DEFAULT 0,
    current_streak INTEGER NOT NULL DEFAULT 0,
    best_streak INTEGER NOT NULL DEFAULT 0,
    total_score INTEGER NOT NULL DEFAULT 0
  )`,

  // Runs table — matches Firestore users/{uid}/runs/{id}
  `CREATE TABLE IF NOT EXISTS runs (
    id TEXT PRIMARY KEY,
    uid TEXT NOT NULL,
    playlist_id TEXT NOT NULL,
    playlist_type TEXT NOT NULL CHECK(playlist_type IN ('warmup', 'practice', 'challenge')),
    completed_at TEXT NOT NULL DEFAULT (datetime('now')),
    total_score INTEGER NOT NULL DEFAULT 0,
    hash TEXT NOT NULL DEFAULT '',
    synced INTEGER NOT NULL DEFAULT 0,
    FOREIGN KEY (uid) REFERENCES users(uid) ON DELETE CASCADE
  )`,

  // Index for querying runs by user, ordered by completion
  `CREATE INDEX IF NOT EXISTS idx_runs_uid_completed
   ON runs(uid, completed_at DESC)`,

  // Level results — normalized from Firestore's embedded array
  `CREATE TABLE IF NOT EXISTS level_results (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    run_id TEXT NOT NULL,
    level_id TEXT NOT NULL,
    score INTEGER NOT NULL DEFAULT 0,
    grade TEXT NOT NULL CHECK(grade IN ('Perfect', 'Good', 'Off')),
    attempts INTEGER NOT NULL DEFAULT 1,
    time_ms INTEGER NOT NULL DEFAULT 0,
    FOREIGN KEY (run_id) REFERENCES runs(id) ON DELETE CASCADE
  )`,

  // Index for querying level results by run
  `CREATE INDEX IF NOT EXISTS idx_level_results_run
   ON level_results(run_id)`,

  // Leaderboard entries — cached from Firestore leaderboards/{id}/entries
  `CREATE TABLE IF NOT EXISTS leaderboard_entries (
    leaderboard_id TEXT NOT NULL,
    uid TEXT NOT NULL,
    display_name TEXT NOT NULL DEFAULT '',
    photo_url TEXT,
    score INTEGER NOT NULL DEFAULT 0,
    playlist_id TEXT NOT NULL DEFAULT '',
    completed_at TEXT NOT NULL DEFAULT (datetime('now')),
    PRIMARY KEY (leaderboard_id, uid)
  )`,

  // Index for leaderboard ordering
  `CREATE INDEX IF NOT EXISTS idx_leaderboard_score
   ON leaderboard_entries(leaderboard_id, score DESC)`,

  // Sync queue — offline actions pending upload (M2-DATA-002)
  `CREATE TABLE IF NOT EXISTS sync_queue (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    action TEXT NOT NULL CHECK(action IN ('save_run', 'update_stats', 'submit_leaderboard')),
    payload TEXT NOT NULL,
    created_at TEXT NOT NULL DEFAULT (datetime('now')),
    retry_count INTEGER NOT NULL DEFAULT 0,
    last_error TEXT
  )`,

  // Schema version tracking
  `CREATE TABLE IF NOT EXISTS schema_version (
    version INTEGER PRIMARY KEY
  )`,

  `INSERT OR IGNORE INTO schema_version (version) VALUES (1)`,
];
